import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AminoAgeService,
  type AminoAgeCorrelationConfig as GenConfig,
  type AminoAgeCorrelationConfigCreate as GenCreate,
  type AminoAgeCorrelationConfigUpdate as GenUpdate,
} from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

// Re-export the generated types with stronger JSONB typing for the UI
export interface CorrelationMatrix {
  [categoryId: string]: { [categoryId: string]: number };
}

export interface CategoryWeights {
  [categoryId: string]: number;
}

export interface CorrelationConfig extends Omit<GenConfig, "correlations" | "weights"> {
  correlations: CorrelationMatrix;
  weights: CategoryWeights | null;
}

export type CorrelationConfigCreate = Omit<CorrelationConfig, "id" | "createdAt" | "updatedAt">;
export type CorrelationConfigUpdate = Partial<CorrelationConfigCreate>;

export interface PreviewUser {
  authUserId: string;
  name: string;
  gender: string;
  chronologicalAge: number;
  aminoAge: number | null;
  ageDifference: number | null;
  availableCategories: number;
  categories: { id: string; name: string; delta: number | null; contribution: number | null; rawValue: number | null; rawUnit: string }[];
}

export interface PreviewResult {
  configName: string;
  users: PreviewUser[];
  /** Eligible users matching the search, across all pages. */
  total: number;
  limit: number;
  offset: number;
}

export const PREVIEW_PAGE_SIZE = 25;

export const useCorrelationConfigs = () =>
  useQuery({
    queryKey: ["correlation-configs"],
    queryFn: () =>
      fetchAll<CorrelationConfig>(AminoAgeService.getApiAminoAgeCorrelationConfigs),
    staleTime: 60_000,
  });

export const useCreateCorrelationConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CorrelationConfigCreate) => {
      const res = await AminoAgeService.postApiAminoAgeCorrelationConfigs({
        body: data as unknown as GenCreate,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to create config");
      return res.data as unknown as CorrelationConfig;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correlation-configs"] }),
  });
};

export const useUpdateCorrelationConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CorrelationConfigUpdate }) => {
      const res = await AminoAgeService.putApiAminoAgeCorrelationConfigsById({
        path: { id },
        body: data as unknown as GenUpdate,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to update config");
      return res.data as unknown as CorrelationConfig;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correlation-configs"] }),
  });
};

export const useActivateCorrelationConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await AminoAgeService.postApiAminoAgeCorrelationConfigsByIdActivate({
        path: { id },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error("Failed to activate config");
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correlation-configs"] }),
  });
};

export const useDeleteCorrelationConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await AminoAgeService.deleteApiAminoAgeCorrelationConfigsById({
        path: { id },
        headers: getAuthHeaders(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correlation-configs"] }),
  });
};

/**
 * `enabled` is required, not optional: this recomputes amino age live for
 * every user on the page, so it must never run unless a human asked for it.
 */
export const useAminoAgePreview = (params: {
  configId?: string;
  offset: number;
  search: string;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: [
      "amino-age-preview",
      params.configId ?? "active",
      params.offset,
      params.search,
    ],
    enabled: params.enabled,
    queryFn: async (): Promise<PreviewResult> => {
      const query: Record<string, string | number> = {
        limit: PREVIEW_PAGE_SIZE,
        offset: params.offset,
      };
      if (params.configId) query.configId = params.configId;
      if (params.search) query.search = params.search;
      const res = await AminoAgeService.getApiAminoAgePreview({
        // The generated client still describes the pre-pagination query
        // shape; drop the cast once `./dev.sh openapi` has been run.
        query: query as unknown as { configId?: string },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Preview failed");
      return res.data as unknown as PreviewResult;
    },
    staleTime: 30_000,
  });
